import React, {memo, useMemo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, fontSize, fontWeight, overlay, transition} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

type StatIcon = 'star' | 'cube' | 'clock' | 'people' | 'trend' | 'globe';

interface MvpStat {
    /** A built-in icon key, or a path to an SVG in /public/images. */
    icon: string;
    value: string;
    label: string;
}

interface MvpStatsSectionProps {
    heading: string;
    subheading?: string;
    stats: MvpStat[];
}

const ICONS: Record<StatIcon, React.ElementType> = {
    star: StarBorderIcon,
    cube: ViewInArOutlinedIcon,
    clock: AccessTimeOutlinedIcon,
    people: PeopleAltOutlinedIcon,
    trend: TrendingUpOutlinedIcon,
    globe: PublicOutlinedIcon
};

const sectionSx = {bgcolor: colors.primary.deep} as const;

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.inverse,
        textAlign: 'center',
        mb: 1.5
    }
] as const;

// With no subheading, the heading itself carries the gap the subheading would have left
// above the cards.
const headingOnlySx = [...headingSx, {mb: {xs: 4, md: 5}}] as const;

const subheadingSx = {color: colors.text.inverse, textAlign: 'center', maxWidth: 640, mx: 'auto', mb: {xs: 4, md: 5}} as const;

const cardWrapSx = {
    bgcolor: overlay.whiteSubtle,
    borderRadius: borderRadius.xl,
    p: {xs: 2, md: 3}
} as const;

// Below `sm` the stats run as one continuously looping row instead of a grid. The list
// is rendered twice back to back and the track slides by exactly one copy (-50%), so the
// loop has no seam. Each copy carries a trailing gap equal to the gap between cards,
// which keeps the two halves identical. From `sm` only the first copy shows, as a grid.
const MARQUEE_GAP_PX = 16;
const MARQUEE_CARD_WIDTH = 190;
const MARQUEE_DURATION_S = 28;

const viewportSx = {
    overflow: {xs: 'hidden', sm: 'visible'},
    // Room for the hover scale on phones.
    py: {xs: 1, sm: 0},
    // Touching or hovering the row holds it still so a stat can be read.
    '&:hover > *, &:active > *': {animationPlayState: 'paused'},
    '@media (prefers-reduced-motion: reduce)': {
        overflowX: {xs: 'auto', sm: 'visible'},
        '& > *': {animation: 'none'},
        '& .mvp-stats-copy': {display: 'none'}
    }
} as const;

const trackSx = {
    display: {xs: 'flex', sm: 'block'},
    width: {xs: 'max-content', sm: 'auto'},
    animation: {xs: `mvpStatsScroll ${MARQUEE_DURATION_S}s linear infinite`, sm: 'none'},
    '@keyframes mvpStatsScroll': {
        from: {transform: 'translateX(0)'},
        to: {transform: 'translateX(-50%)'}
    }
} as const;

const statListSx = {
    display: {xs: 'flex', sm: 'grid'},
    gap: {xs: `${MARQUEE_GAP_PX}px`, sm: 2},
    pr: {xs: `${MARQUEE_GAP_PX}px`, sm: 0},
    // Phones: fixed-width cards that never shrink, so the row keeps its length.
    '& > *': {flexShrink: 0, width: {xs: MARQUEE_CARD_WIDTH, sm: 'auto'}}
} as const;

const statCopySx = [statListSx, {display: {xs: 'flex', sm: 'none'}}] as const;

// Desktop shows every stat in one row, so the column count follows the number of stats
// rather than a fixed six — five stats fill the container instead of leaving a gap.
const getColumnsSx = (count: number) => ({
    gridTemplateColumns: {sm: 'repeat(3, minmax(0, 1fr))', lg: `repeat(${Math.max(count, 1)}, minmax(0, 1fr))`}
});

const statCardSx = {
    bgcolor: overlay.whiteSubtle,
    borderRadius: borderRadius.lg,
    border: `1px solid ${overlay.whiteMid}`,
    p: {xs: 2.5, md: 3, lg: 2},
    height: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: `background-color ${transition.slow}, border-color ${transition.slow}, transform ${transition.slow}`,
    '&:hover': {
        bgcolor: overlay.whiteLight,
        borderColor: overlay.whiteBorder,
        transform: 'scale(1.04)'
    },
    '&:hover .mvp-stat-value': {
        color: colors.accent.orange
    },
    '&:hover .mvp-stat-icon-tile': {
        bgcolor: colors.background.default
    },
    '&:hover .mvp-stat-icon': {
        color: `${colors.primary.deep} !important`
    },
    // File-based icons are painted with `mask-image`, so they recolour with the same
    // hover as the built-in MUI icons.
    '&:hover .mvp-stat-icon-img': {
        bgcolor: colors.primary.deep
    }
} as const;

const iconTileSx = {
    width: 40,
    height: 40,
    borderRadius: borderRadius.md,
    bgcolor: overlay.whiteLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 2,
    transition: `background-color ${transition.slow}`
} as const;

const valueSx = {
    fontWeight: fontWeight.extrabold,
    // All six share one row from `lg`, so the cards are at their narrowest there: the
    // number steps down so the longest value ("10–12 Weeks") still fits on one line.
    fontSize: {xs: fontSize[22], sm: fontSize[28], lg: fontSize[20], xl: fontSize[24]},
    whiteSpace: 'nowrap',
    mb: 1,
    color: colors.text.inverse,
    transition: `color ${transition.slow}`
} as const;
const labelSx = {color: colors.text.inverse, fontWeight: fontWeight.semibold, fontSize: fontSize[14]} as const;

const StatCard = ({stat}: {stat: MvpStat}) => {
    // Anything that is not one of the built-in keys is a file: a local
    // path or, since the assets moved to the CDN, an absolute URL.
    const isImage = !(stat.icon in ICONS);
    // CDN URLs already carry their own percent-encoding; only a raw
    // local path with a space still needs encoding here.
    const iconUrl = stat.icon.includes(' ') ? encodeURI(stat.icon) : stat.icon;
    const Icon = ICONS[stat.icon as StatIcon];
    return (
        <Box sx={statCardSx}>
            <Box sx={iconTileSx} className={`mvp-stat-icon-tile${isImage ? ' mvp-stat-icon-tile--img' : ''}`}>
                {isImage ? (
                    // The SVG is a single-colour glyph, so it is drawn as a mask over a
                    // solid box: `<img>` would keep its baked-in white on hover.
                    <Box
                        className="mvp-stat-icon-img"
                        aria-hidden="true"
                        sx={{
                            width: 22,
                            height: 22,
                            bgcolor: colors.text.inverse,
                            transition: `background-color ${transition.slow}`,
                            // Quoted and encoded: some icon filenames contain spaces,
                            // which silently break a bare `url(...)` and leave the box
                            // painted as a solid square.
                            maskImage: `url("${iconUrl}")`,
                            WebkitMaskImage: `url("${iconUrl}")`,
                            maskRepeat: 'no-repeat',
                            WebkitMaskRepeat: 'no-repeat',
                            maskPosition: 'center',
                            WebkitMaskPosition: 'center',
                            maskSize: 'contain',
                            WebkitMaskSize: 'contain'
                        }}
                    />
                ) : (
                    <Icon
                        className="mvp-stat-icon"
                        sx={{
                            color: colors.text.inverse,
                            fontSize: fontSize[20],
                            transition: `color ${transition.slow}`
                        }}
                    />
                )}
            </Box>
            <Typography component="p" className="font-headers mvp-stat-value" sx={valueSx}>
                {stat.value}
            </Typography>
            <Typography component="p" className="font-headers" sx={labelSx}>
                {stat.label}
            </Typography>
        </Box>
    );
};

const MvpStatsSection = ({heading, subheading, stats}: MvpStatsSectionProps) => {
    const statCount = stats?.length ?? 0;
    const listSx = useMemo(() => [statListSx, getColumnsSx(statCount)], [statCount]);

    return (
        <Box sx={sectionSx}>
            <MUIContainer>
                <Typography variant="h1" component="h2" className="font-headers" sx={subheading ? headingSx : headingOnlySx}>
                    {heading}
                </Typography>
                {subheading && (
                    <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                        {subheading}
                    </Typography>
                )}

                <Box sx={cardWrapSx}>
                    <Box sx={viewportSx}>
                        <Box sx={trackSx}>
                            <Box sx={listSx}>
                                {stats?.map((stat) => (
                                    <StatCard key={stat.label} stat={stat} />
                                ))}
                            </Box>
                            {/* Second copy for the seamless phone loop only: decorative, so it is
                                hidden from assistive tech and never shown from `sm`. */}
                            <Box sx={statCopySx} className="mvp-stats-copy" aria-hidden="true">
                                {stats?.map((stat) => (
                                    <StatCard key={stat.label} stat={stat} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpStatsSection);
